import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import styles from './AddShorts.module.css';
import { useCategories } from '../../../hooks/categories/useCategories';
import { useCreateShort } from '../../../hooks/admin/useAdminShorts';
import { useNavigate } from 'react-router-dom';

const AddShorts = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    is_public: true,
    is_featured: false,
    meta_description: '',
    meta_keywords: '',
    page_title: ''
  });
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  
  const { categories = [] } = useCategories('Shorts');
  const { createShort, isLoading } = useCreateShort();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnail(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const removeVideo = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    setVideoFile(null);
    setVideoPreview(null);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const removeThumbnail = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    setThumbnail(null);
    setThumbnailPreview(null);
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!videoFile) {
      toast.error('Please select a video file');
      return;
    }

    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      formDataToSend.append(key, formData[key]);
    });
    formDataToSend.append('video', videoFile);
    if (thumbnail) {
      formDataToSend.append('thumbnail', thumbnail);
    }

    try {
      await createShort(formDataToSend);
      // Reset form
      setFormData({
        title: '',
        description: '',
        category_id: '',
        is_public: true,
        is_featured: false,
        meta_description: '',
        meta_keywords: '',
        page_title: ''
      });
      removeVideo({ stopPropagation: () => {} }); // Pass mock event object
      removeThumbnail({ stopPropagation: () => {} }); // Pass mock event object
    } catch (error) {
      toast.error(error.message || 'Failed to create short');
    }
  };

  const triggerVideoInput = () => {
    if (videoInputRef.current) {
      videoInputRef.current.click();
    }
  };

  const triggerThumbnailInput = () => {
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.click();
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Add New Short</h1>
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.inputGroup}>
          <label htmlFor="title">Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
          />
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="category">Category</label>
          <select
            id="category"
            name="category_id"
            value={formData.category_id}
            onChange={handleInputChange}
          >
            <option value="">Select Category</option>
            {categories?.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="page_title">Page Title</label>
          <input
            type="text"
            id="page_title"
            name="page_title"
            value={formData.page_title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="meta_description">Meta Description</label>
          <textarea
            id="meta_description"
            name="meta_description"
            value={formData.meta_description}
            onChange={handleInputChange}
            rows="2"
            required
          />
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="meta_keywords">Meta Keywords (comma separated)</label>
          <input
            type="text"
            id="meta_keywords"
            name="meta_keywords"
            value={formData.meta_keywords}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={styles.mediaSection}>
          <div className={styles.uploadBox}>
            <label>Video Upload *</label>
            <input
              type="file"
              ref={videoInputRef}
              accept="video/*"
              onChange={handleVideoChange}
              className={styles.fileInput}
              style={{ display: 'none' }} // Hide the native input
            />
            {!videoPreview ? (
              <div 
                className={styles.uploadPlaceholder}
                onClick={triggerVideoInput}
              >
                <Upload size={40} />
                <p>Click to upload video</p>
              </div>
            ) : (
              <div className={styles.previewContainer}>
                <video src={videoPreview} controls className={styles.videoPreview} />
                <button
                  type="button"
                  onClick={removeVideo}
                  className={styles.removeButton}
                  aria-label="Remove video"
                >
                  <X size={20} />
                </button>
              </div>
            )}
          </div>

          <div className={styles.uploadBox}>
            <label>Thumbnail</label>
            <input
              type="file"
              ref={thumbnailInputRef}
              accept="image/*"
              onChange={handleThumbnailChange}
              className={styles.fileInput}
              style={{ display: 'none' }} // Hide the native input
            />
            {!thumbnailPreview ? (
              <div 
                className={styles.uploadPlaceholder}
                onClick={triggerThumbnailInput}
              >
                <ImageIcon size={40} />
                <p>Click to upload thumbnail</p>
              </div>
            ) : (
              <div className={styles.previewContainer}>
                <img src={thumbnailPreview} alt="Thumbnail preview" className={styles.thumbnailPreview} />
                <button
                  type="button"
                  onClick={removeThumbnail}
                  className={styles.removeButton}
                  aria-label="Remove thumbnail"
                >
                  <X size={20} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.checkboxGroup}>
          <label>
            <input
              type="checkbox"
              name="is_public"
              checked={formData.is_public}
              onChange={handleInputChange}
            />
            Make this short public
          </label>
          
          <label>
            <input
              type="checkbox"
              name="is_featured"
              checked={formData.is_featured}
              onChange={handleInputChange}
            />
            Feature this short
          </label>
        </div>

        <button
          type="submit"
          className={styles.submitButton}
          disabled={isLoading}
        >
          {isLoading ? 'Creating...' : 'Create Short'}
        </button>
      </form>
    </div>
  );
};

export default AddShorts;